import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, Output, EventEmitter, ElementRef, HostListener, forwardRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormControl } from '@angular/forms';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { BaseUnsubscribeComponent } from '../../shared-new/components/base-unsubscribe/base-unsubscribe.component';

export class LookupModel {
  public id: number;
  public name: string;
  public altName: string;
  public selected: boolean;
}

interface FilteredItem {
  model: LookupModel;
  isSelectedTop?: boolean;
}

@Component({
  selector: 'app-search-dropdown',
  templateUrl: './search-dropdown.component.html',
  styleUrls: ['./search-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchDropdownComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InfiniteScrollModule
  ],
})
export class SearchDropdownComponent extends BaseUnsubscribeComponent implements OnInit, ControlValueAccessor {
  private _items: LookupModel[] = [];
  @Input() public set items(value: LookupModel[]) {
    if (this.sortAsc)
      value = this.sortItemsAsc(value);
    else {
      value = this.sortItemsDesc(value);
    }
    this._items = value;
    this.onLoadItems();
  }
  public get items(): LookupModel[] {
    return this._items;
  }
  @Input() sortAsc: boolean = true; // Sort order
  @Input() allOption: string = ''; // Text for the 'all' option (optional)
  @Input() placeholder: string = ''; // Placeholder text
  @Input() label: string = ''; // Form label
  @Input() limit: number = 10; // Max items to show in dropdown
  @Output() selectionChange: EventEmitter<LookupModel | null> = new EventEmitter(); // Emit selected model (null for 'all')
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement> | undefined;

  searchControl = new FormControl('');
  searchText: string = '';
  showDropdown: boolean = false;
  filteredItems: FilteredItem[] = [];
  highlightedItem: LookupModel | null = null;
  allItems: LookupModel[] = []; // Internal full list including 'all' if applicable
  allOptionModel: LookupModel | undefined; // Model for the 'all' option (undefined if not used)

  public selectedItem: LookupModel | null = null;


  // ControlValueAccessor implementation
  private onChange: (value: LookupModel | null) => void = () => { };
  private onTouched: () => void = () => { };
  private disabled: boolean = false;
  private static openedDropdown: SearchDropdownComponent | null = null;
  constructor(private eRef: ElementRef) {
      super();
  }

  ngOnInit(): void {
    // Optional: Call onLoadItems() here if items might be set before setter
  }

  private onLoadItems() {
    if (this.allOption) {
      this.allOptionModel = {
        id: 0, // Assuming 0 for 'all', adjust if needed
        name: this.allOption,
        altName: '',
        selected: false
      };
      this.allItems = [this.allOptionModel, ...this.items];
    } else {
      this.allOptionModel = undefined;
      this.allItems = [...this.items];
    }
  
    const selectedItem = this.items.find(i => i.selected);
    this.setSelectedItem(selectedItem || null);
    this.loadInitialItems();
    this.searchControl.valueChanges.subscribe(value => {
      this.searchText = value || '';
      this.filterItems();
    });
  }

  onDropDownScroll() {
    const searchText = (this.searchControl.value || '').toLowerCase();

    // Filter all items based on the current search text
    const matchingItems = this.items.filter(m =>
      m.name.toLowerCase().includes(searchText) ||
      (m.altName && m.altName.toLowerCase().includes(searchText))
    );

    // Exclude the selected item if it’s already displayed at the top
    const filteredList = this.selectedItem
      ? matchingItems.filter(m => m.id !== this.selectedItem.id)
      : matchingItems;

    // Figure out how many items are already shown
    const currentLength = this.filteredItems.length;

    // Take the next batch according to the scroll limit
    const nextItems = filteredList.slice(currentLength, currentLength + this.limit);

    // Append to the filtered list
    if (nextItems.length > 0) {
      this.filteredItems = [
        ...this.filteredItems,
        ...nextItems.map(m => ({ model: m }))
      ];
    }
  }




  private setSelectedItem(selectedItem: LookupModel) {
    if (selectedItem) {
      this.selectedItem = selectedItem;
      this.searchControl.setValue(selectedItem.name);
      this.searchText = selectedItem.name;
    } else {
      this.selectedItem = null;
      this.searchControl.setValue('');
      this.searchText = '';
    }
  }

  private sortItemsAsc(items: LookupModel[]) {
    return items.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;

      const altA = (a.altName || '').toLowerCase();
      const altB = (b.altName || '').toLowerCase();
      if (altA < altB) return -1;
      if (altA > altB) return 1;

      return 0;
    });
  }

  private sortItemsDesc(items: LookupModel[]) {
    return items.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (nameA > nameB) return -1;
      if (nameA < nameB) return 1;

      const altA = (a.altName || '').toLowerCase();
      const altB = (b.altName || '').toLowerCase();
      if (altA > altB) return -1;
      if (altA < altB) return 1;

      return 0;
    });
  }


  loadInitialItems() {
    this.filterItems(); // Use the same logic as filter with empty search
  }

  filterItems() {
    const hasAll = !!this.allOptionModel;
    let displayFiltered: FilteredItem[] = [];
    let filteredModels: LookupModel[];

    if (!this.searchText) {
      filteredModels = this.items.slice(0, this.limit);
    } else {
      const lowerSearch = this.searchText.toLowerCase();
      filteredModels = this.items
        .filter(item =>
          item.name.toLowerCase().includes(lowerSearch) ||
          (item.altName && item.altName.toLowerCase().includes(lowerSearch))
        )
        .slice(0, this.limit);
     
    }

    if (this.selectedItem) {
      // Always add selected at top, even if it doesn't match filter or is beyond limit
      const selectedWrapped: FilteredItem = { model: this.selectedItem, isSelectedTop: true };
      displayFiltered.push(selectedWrapped);

      // Remove selected from filteredModels if it's already there to avoid duplication
      filteredModels = filteredModels.filter(m => m.id !== this.selectedItem!.id);

      // Adjust limit for the rest
      const adjustedLimit = this.limit - 1; // Since selected takes one spot
      displayFiltered = [...displayFiltered, ...filteredModels.slice(0, adjustedLimit).map(m => ({ model: m }))];
    } else {
      displayFiltered = filteredModels.map(m => ({ model: m }));
    }

    this.filteredItems = hasAll ? [{ model: this.allOptionModel! }, ...displayFiltered] : displayFiltered;
  }

  selectItem(item: LookupModel) {
    let selectedValue: LookupModel | null = item;
    if (this.allOptionModel && item.id === this.allOptionModel.id) {
      selectedValue = null;
      this.searchControl.setValue('');
      this.searchText = '';
    } else {
      this.searchControl.setValue(item.name);
      this.searchText = item.name;
    }
    this.selectedItem = selectedValue;
    if (this.selectedItem) {
      this.items.forEach(i => i.selected = this.selectedItem ? i.id === this.selectedItem.id : false);
    }
    this.showDropdown = false;
    this.selectionChange.emit(selectedValue);
    this.onChange(selectedValue); // Notify form of value change
    this.onTouched(); // Mark as touched
  }


  

  focusDropDown() {
    if (this.showDropdown && this.searchInput) {
      this.searchInput.nativeElement.focus();
    }
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();

    if (!this.showDropdown) {
      // Close any other open dropdown first 👇
      if (SearchDropdownComponent.openedDropdown && SearchDropdownComponent.openedDropdown !== this) {
        SearchDropdownComponent.openedDropdown.closeDropdownExternally();
      }

      SearchDropdownComponent.openedDropdown = this;
      this.searchControl.setValue('');
      this.searchText = '';
      this.filterItems();
      this.highlightedItem = this.selectedItem;
      this.showDropdown = true;
    } else {
      this.restoreValue();
      this.showDropdown = false;
      SearchDropdownComponent.openedDropdown = null;
    }
  }

  closeDropdownExternally() {
    this.showDropdown = false;
    this.restoreValue();
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.closeDropdownExternally();
      if (SearchDropdownComponent.openedDropdown === this) {
        SearchDropdownComponent.openedDropdown = null;
      }
    }
  }

  private restoreValue() {
    const restoreText = this.selectedItem ? this.selectedItem.name : '';
    this.searchControl.setValue(restoreText);
    this.searchText = restoreText;
    // No need to filterItems() since dropdown is closed
  }

  clearSelection() {
    this.selectedItem = null;
    this.searchControl.setValue('');
    this.searchText = '';
    this.selectionChange.emit(null);
    this.onChange(null);
    this.onTouched();
    this.filterItems(); // Refresh dropdown if open
    this.focusDropDown();
  }
  handleKeydown(event: KeyboardEvent) {
    if (this.showDropdown) {
      // If open, handle navigation (add if needed, e.g., Arrow Up/Down to highlight)
      // For now, stop propagation if you want to prevent bubbling
      event.stopPropagation();
      return;
    }

    // If closed, check if printable key to open (ignore modifiers like Ctrl, Alt)
    const key = event.key;
    const isPrintable = key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
    if (isPrintable) {
      // Open without clearing value (let the typed char filter)
      this.showDropdown = true;
      this.filterItems(); // Or rely on valueChanges
      event.stopPropagation(); // Optional
    }
  }
  // ControlValueAccessor methods
  writeValue(value: LookupModel | null): void {
    this.selectedItem = value;
    const displayText = value ? value.name : '';
    this.searchControl.setValue(displayText);
    this.searchText = displayText;
  }

  registerOnChange(fn: (value: LookupModel | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.searchControl[isDisabled ? 'disable' : 'enable']();
  }
}
